import { Link, useForm } from "@inertiajs/react";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import FormField from "@/components/forms/FormField";
import SelectField from "@/components/forms/SelectField";

function SettingsFormPreview() {
  const { data, setData } = useForm({
    name: "Lincoln Elementary",
    address: "123 Yonge St",
    city: "Toronto",
    province: "ON",
    postal_code: "M5B 2H1",
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>School details</CardTitle>
        <CardDescription>Your school's name is on every donation page, receipt and sheet that goes home.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-5">
          <FormField name="name" label="School name" required value={data.name} onChange={(e) => setData("name", e.target.value)} />
          <FormField name="address" label="Address" value={data.address} onChange={(e) => setData("address", e.target.value)} />
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField name="city" label="City" value={data.city} onChange={(e) => setData("city", e.target.value)} />
            <SelectField name="province" label="Province or territory" value={data.province} onChange={(e) => setData("province", e.target.value)} options={["ON", "BC", "AB"]} />
          </div>
          <FormField name="postal_code" label="Postal code" value={data.postal_code} onChange={(e) => setData("postal_code", e.target.value)} />
          <Button>Save changes</Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default function SchoolSettings() {
  return (
    <>
      <p>
        Your <strong>Settings</strong> page is for the slow-changing stuff: your school's name, address, and timezone — the things that show up on take-home sheets, receipts, and donor pages.
      </p>

      <Screenshot caption="School details">
        <div className="max-w-md mx-auto">
          <SettingsFormPreview />
        </div>
      </Screenshot>

      <p>
        <strong>School name</strong> shows up everywhere — on take-home sheets, on receipts, in emails. Pick the name parents would actually call your school.
      </p>
      <p>
        <strong>Address</strong> shows up on donor receipts so they know which "Lincoln Elementary" they donated to (handy if there are several in your city). No legal weight, it's just for clarity.
      </p>
      <p>
        <strong>Timezone</strong> tells us when "today" is for your school: when each campaign day starts and ends, and when minute logging closes. It starts as your province's usual zone. If you change it, campaigns that haven't started keep their dates.
      </p>

      <Callout variant="tip" title="Try not to rename the school mid-campaign">
        <p>
          It's safe to do — donations, take-home sheets, and parent links all keep working. But anyone who has your dashboard open in a tab will see a "page not found" until they refresh. Save big changes for between campaigns if you can.
        </p>
      </Callout>

      <p>
        Settings is also where you manage your <strong>Square connection</strong>. You can connect it once your first campaign is paid for and approved. Disconnecting asks first, because nobody can donate until you connect again.
      </p>
      <p>
        <strong>Notifications</strong> chooses which automatic emails go to your school admins, teachers and families. Each one has a preview.
      </p>
      <p>
        The <strong>campaign</strong> card shows where your campaign is (getting ready, live, wrapping up or finished), its schedule in your timezone, and — if you ever need it — a way to move a live campaign on early. Campaigns launch by themselves on their start date, so there's no launch button.
      </p>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/managing-members">Managing school admins and teachers</Link>.</li>
        <li><Link href="/help/articles/connecting-square">Connecting Square</Link>.</li>
      </ul>
    </>
  );
}
